import {
  useGetMentorQualityScores,
  useGetMentorWidgetStatistics,
  useSearchMentors,
} from '@actions';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import MentorsTableHeader from '@components/V3/MentorsTableHeader';
import MentorsTableRow from '@components/V3/MentorsTableRow';
import TableColumnSortButtons from '@components/V3/TableColumnSortButtons';
import UsersTableInfo from '@components/V3/UsersTableInfo';
import AgoraSpinner from '@components/V3/Utils/AgoraSpinner';
import { TriState } from '@shared/clientTypes/misc';
import { Pagination } from 'antd';
import { createContext, useMemo, useState } from 'react';
import { logger } from '../../../utils/logger';
import { COLORS } from '../../../v3/global';
import './mentors-v3.styles.scss';
import DetailsModal, { DetailsModalPage } from '../components/DetailsModal/DetailsModal';
import { useAuthState } from '@contexts/AuthContext';
import { UserRole } from '@shared/enums';

interface MentorPageSessionModal {
  showEnrollmentPageModalHandler?: (enrollmentId: string) => void;
  showStudentPageModalHandler?: (studentId: string) => void;
  showSessionPageModalHandler?: (sessionId: string) => void;
}

export const MentorPageSessionModal = createContext<MentorPageSessionModal>({
  showEnrollmentPageModalHandler: () =>
    logger.warn('handleShowEnrollmentPageModal not implemented'),
  showStudentPageModalHandler: () => logger.warn('handleShowEnrollmentPageModal not implemented'),
  showSessionPageModalHandler: () => logger.warn('handleShowEnrollmentPageModal not implemented'),
});

const MentorsV3 = () => {
  const { roles } = useAuthState();
  const isBdr = roles?.includes(UserRole.BDR);
  const { data: mentorQualityScores } = useGetMentorQualityScores(undefined, { enabled: !isBdr });
  const qualityScoreByMentor = useMemo(
    () => new Map((mentorQualityScores ?? []).map((row) => [row.mentorId, row])),
    [mentorQualityScores]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [course, setCourse] = useState<any>([]);
  const [isTestUser, setIsTestUser] = useState<TriState>('unchecked');
  const [isDeactivated, setIsDeactivated] = useState<TriState>('unchecked');
  const [extraFilter, setExtraFilter] = useState(undefined);
  const [majors, setMajors] = useState<string[]>([]);
  const [degreeType, setDegreeType] = useState<string>();
  const [universities, setUniversities] = useState<string[]>([]);
  const [spokenLanguages, setSpokenLanguages] = useState<string[]>([]);
  const [studyLanguages, setStudyLanguages] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [payRateMin, setPayRateMin] = useState('');
  const [payRateMax, setPayRateMax] = useState('');

  const [mentorId, setMentorId] = useState<string>();
  const [studentId, setStudentId] = useState<string>();
  const [enrollmentId, setEnrollmentId] = useState<string>();
  const [sessionId, setSessionId] = useState<string>();
  const [modalPage, setModalPage] = useState<DetailsModalPage>('enrollment');
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [columnsSorting, setColumnsSorting] = useState({
    capacity: 'default',
    maxCapacity: 'default',
    maxCapacityUpdatedAt: 'default',
    payRate: 'default',
    createdAt: 'default',
  });

  const {
    data: mentorStatistics,
    isLoading: isLoadingMentorStatistics,
    refetch: refetchMentorStatistics,
  } = useGetMentorWidgetStatistics();

  const {
    data: mentors,
    isLoading: isLoadingMentors,
    refetch: refetchSearchMentors,
  } = useSearchMentors({
    page: currentPage,
    limit: pageSize,
    search: search,
    statuses: statuses.length !== 0 ? statuses : undefined,
    courses: course,
    isTestUser: isTestUser === 'indeterminate' ? undefined : isTestUser === 'checked',
    isDeactivated: isDeactivated === 'indeterminate' ? undefined : isDeactivated === 'checked',
    sorting: { ...columnsSorting },
    extraFilter: extraFilter,
    majors: majors.length !== 0 ? majors : undefined,
    degreeType: degreeType,
    universities: universities.length !== 0 ? universities : undefined,
    spokenLanguages: spokenLanguages.length !== 0 ? spokenLanguages : undefined,
    studyLanguages: studyLanguages.length !== 0 ? studyLanguages : undefined,
    countries: countries.length !== 0 ? countries : undefined,
    payRateMin:
      payRateMin !== '' && (!payRateMax || Number(payRateMin) <= Number(payRateMax))
        ? Number(payRateMin)
        : undefined,
    payRateMax:
      payRateMax !== '' && (!payRateMin || Number(payRateMin) <= Number(payRateMax))
        ? Number(payRateMax)
        : undefined,
  });

  const handleColumnSorting = (column: any) => {
    if (column === 'capacity' && columnsSorting.capacity === 'default') {
      setColumnsSorting({
        capacity: 'asc',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (column === 'capacity' && columnsSorting.capacity === 'asc') {
      setColumnsSorting({
        capacity: 'desc',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (column === 'maxCapacity' && columnsSorting.maxCapacity === 'default') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'asc',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (column === 'maxCapacity' && columnsSorting.maxCapacity === 'asc') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'desc',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (
      column === 'maxCapacityUpdatedAt' &&
      columnsSorting.maxCapacityUpdatedAt === 'default'
    ) {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'asc',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (column === 'maxCapacityUpdatedAt' && columnsSorting.maxCapacityUpdatedAt === 'asc') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'desc',
        payRate: 'default',
        createdAt: 'default',
      });
    } else if (column === 'payRate' && columnsSorting.payRate === 'default') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'asc',
        createdAt: 'default',
      });
    } else if (column === 'payRate' && columnsSorting.payRate === 'asc') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'desc',
        createdAt: 'default',
      });
    } else if (column === 'createdAt' && columnsSorting.createdAt === 'default') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'asc',
      });
    } else if (column === 'createdAt' && columnsSorting.createdAt === 'asc') {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'desc',
      });
    } else {
      setColumnsSorting({
        capacity: 'default',
        maxCapacity: 'default',
        maxCapacityUpdatedAt: 'default',
        payRate: 'default',
        createdAt: 'default',
      });
    }
  };

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
    setCurrentPage(1);
  };

  const handleAddStatus = (statusValue: string) => {
    setStatuses((prevState) => [...prevState, statusValue]);
    setCurrentPage(1);
  };

  const handleRemoveStatus = (statusValue: string) => {
    setStatuses((prevState) => statuses.filter((status) => status !== statusValue));
    setCurrentPage(1);
  };

  const handleClearStatuses = () => {
    setStatuses([]);
    setCurrentPage(1);
  };

  const handleCourse = (course: any) => {
    setCourse([course]);
    setCurrentPage(1);
  };

  const handleClearCourse = () => {
    setCourse([]);
    setCurrentPage(1);
  };

  const handleDegreeType = (degreeType: string) => {
    setDegreeType(degreeType);
    setCurrentPage(1);
  };

  const handleClearDegreeType = () => {
    setDegreeType(undefined);
    setCurrentPage(1);
  };

  const handleAddUniversity = (university: string) => {
    setUniversities((prev) => [...prev, university]);
    setCurrentPage(1);
  };

  const handleRemoveUniversity = (university: string) => {
    setUniversities((prev) => prev.filter((item) => item !== university));
    setCurrentPage(1);
  };

  const handleSpokenLanguagesChange = (languages: string[]) => {
    setSpokenLanguages(languages);
    setCurrentPage(1);
  };

  const handleStudyLanguagesChange = (languages: string[]) => {
    setStudyLanguages(languages);
    setCurrentPage(1);
  };

  const handleCountriesChange = (selectedCountries: string[]) => {
    setCountries(selectedCountries);
    setCurrentPage(1);
  };

  const handleClearUniversity = () => {
    setUniversities([]);
    setCurrentPage(1);
  };

  const handleIsTestUser = (value: TriState) => {
    setIsTestUser(value);
    setCurrentPage(1);
  };

  const handleIsDeactivated = (value: TriState) => {
    setIsDeactivated(value);
    setCurrentPage(1);
  };

  const handleExtraFilter = (extraFilter: any) => {
    setExtraFilter(extraFilter);
    setCurrentPage(1);
  };

  const handlePayRateMinChange = (value: string) => {
    setPayRateMin(value);
    setCurrentPage(1);
  };

  const handlePayRateMaxChange = (value: string) => {
    setPayRateMax(value);
    setCurrentPage(1);
  };

  const handleClearMajors = () => {
    setMajors([]);
    setCurrentPage(1);
  };

  const handleAddMajor = (major: string) => {
    setMajors((prevState) => [...prevState, major]);
    setCurrentPage(1);
  };

  const handleRemoveMajor = (major: string) => {
    setMajors((prevState) => prevState.filter((item) => item !== major));
    setCurrentPage(1);
  };

  const showEnrollmentPageModalHandler = (enrollmentId: string) => {
    setEnrollmentId(enrollmentId);
    setShowDetailsModal(true);
    setModalPage('enrollment');
  };

  const showStudentPageModalHandler = (studentId: string) => {
    setStudentId(studentId);
    setShowDetailsModal(true);
    setModalPage('student');
  };

  const showSessionPageModalHandler = (sessionId: string) => {
    setSessionId(sessionId);
    setShowDetailsModal(true);
    setModalPage('session');
  };

  const showMentorPageModalHandler = (mentorId: string) => {
    setMentorId(mentorId);
    setShowDetailsModal(true);
    setModalPage('mentor');
  };

  const handleCloseModal = () => {
    setStudentId(undefined);
    setShowDetailsModal(false);
    setModalPage(undefined);
  };

  return (
    <div
      style={{
        backgroundColor: COLORS.SECTION_BACKGROUND,
        color: COLORS.TEXT_PRIMARY,
        minHeight: 'calc(100vh - 74px)',
      }}
    >
      {/*TABLE HEADER*/}
      <UsersTableInfo
        mentorStatistics={mentorStatistics}
        handleSearch={handleSearch}
        search={search}
        handleAddStatus={handleAddStatus}
        handleRemoveStatus={handleRemoveStatus}
        statuses={statuses}
        handleCourse={handleCourse}
        handleClearCourse={handleClearCourse}
        course={course}
        handleIsTestUser={handleIsTestUser}
        isDeactivated={isDeactivated === 'indeterminate' ? undefined : isDeactivated === 'checked'}
        handleIsDeactivated={handleIsDeactivated}
        isTestUser={isTestUser === 'indeterminate' ? undefined : isTestUser === 'checked'}
        handleExtraFilter={handleExtraFilter}
        extraFilter={extraFilter}
        handleAddMajor={handleAddMajor}
        handleRemoveMajor={handleRemoveMajor}
        handleClearMajors={handleClearMajors}
        majors={majors}
        degreeType={degreeType}
        handleDegreeType={handleDegreeType}
        handleClearDegreeType={handleClearDegreeType}
        handleClearStatuses={handleClearStatuses}
        isLoadingMentorStatistics={isLoadingMentorStatistics}
        universities={universities}
        handleAddUniversity={handleAddUniversity}
        handleRemoveUniversity={handleRemoveUniversity}
        handleClearUniversity={handleClearUniversity}
        spokenLanguages={spokenLanguages}
        handleSpokenLanguagesChange={handleSpokenLanguagesChange}
        studyLanguages={studyLanguages}
        handleStudyLanguagesChange={handleStudyLanguagesChange}
        countries={countries}
        handleCountriesChange={handleCountriesChange}
        payRateMin={payRateMin}
        payRateMax={payRateMax}
        handlePayRateMinChange={handlePayRateMinChange}
        handlePayRateMaxChange={handlePayRateMaxChange}
      />

      {showDetailsModal && (
        <DetailsModal
          mentorId={mentorId}
          studentId={studentId}
          enrollmentId={enrollmentId}
          sessionId={sessionId}
          page={modalPage}
          isOpen={true}
          onClose={handleCloseModal}
        />
      )}

      {/*TABLE*/}
      <div className="overflow-auto">
        <table className="mentors-table">
          {/*TABLE COLUMNS*/}
          <MentorsTableHeader>
            <tr>
              <th style={{ textWrap: 'nowrap' }}>
                <span>First Name</span>
              </th>
              <th style={{ textWrap: 'nowrap' }}>
                <span>Last Name</span>
              </th>
              {isBdr && (
                <th>
                  <span>Email</span>
                </th>
              )}
              <th>
                <span>Status</span>
              </th>

              <th className="" onClick={() => handleColumnSorting('capacity')}>
                <div>
                  <span>Capacity</span>
                  <TableColumnSortButtons sortType={columnsSorting.capacity} />
                </div>
              </th>
              <th className="" onClick={() => handleColumnSorting('maxCapacity')}>
                <div>
                  <span className="table-head-span">Max Cap.</span>
                  <TableColumnSortButtons sortType={columnsSorting.maxCapacity} />
                </div>
              </th>
              <th className="" onClick={() => handleColumnSorting('maxCapacityUpdatedAt')}>
                <div>
                  <span className="table-head-span">Cap. Last Update</span>
                  <TableColumnSortButtons sortType={columnsSorting.maxCapacityUpdatedAt} />
                </div>
              </th>
              <th>
                <span className="table-head-span">Cap. Gap</span>
              </th>
              {!isBdr && (
                <th style={{ textWrap: 'nowrap' }}>
                  <span className="table-head-span">AI Score</span>
                </th>
              )}
              {!isBdr && (
                <th className="" onClick={() => handleColumnSorting('payRate')}>
                  <div>
                    <span className="table-head-span">Pay Rate</span>
                    <TableColumnSortButtons sortType={columnsSorting.payRate} />
                  </div>
                </th>
              )}
              <th>
                <span>Universities</span>
              </th>
              <th>
                <span>Courses</span>
              </th>
              <th>
                <div>
                  <span>Majors</span>
                </div>
              </th>
              <th>
                <div>
                  <span>Degrees</span>
                </div>
              </th>
              <th
                style={{ textWrap: 'nowrap' }}
                className=""
                onClick={() => handleColumnSorting('createdAt')}
              >
                <div>
                  <span className="table-head-span">Created At</span>
                  <TableColumnSortButtons sortType={columnsSorting.createdAt} />
                </div>
              </th>
              {!isBdr && (
                <th>
                  <span>Access</span>
                </th>
              )}
            </tr>
          </MentorsTableHeader>

          {/*TABLE ROWS*/}
          <MentorPageSessionModal.Provider
            value={{
              showEnrollmentPageModalHandler,
              showSessionPageModalHandler,
              showStudentPageModalHandler,
            }}
          >
            <tbody>
              {!isLoadingMentors ? (
                mentors?.mentors?.map((item) => (
                  <MentorsTableRow
                    key={item?.mentor?._id}
                    mentorData={item}
                    qualityScore={qualityScoreByMentor.get(item?.mentor?._id)}
                    refetchSearchMentors={refetchSearchMentors}
                    refetchMentorStatistics={refetchMentorStatistics}
                    onProfileModalOpen={() => showMentorPageModalHandler(item?.mentor?._id)}
                  />
                ))
              ) : (
                <AgoraSpinner fontSize={32} margin="32px" />
              )}
            </tbody>
          </MentorPageSessionModal.Provider>
        </table>
      </div>

      {/*PAGINATION*/}
      <div
        style={{
          width: '100%',
          padding: '24px 24px 120px 24px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <span
            style={{ color: COLORS.TEXT_SECONDARY, marginRight: '8px' }}
            className="mentors-table-pagination-label"
          >
            Rows per page
          </span>
          <select
            onChange={(e) => {
              const selectedValue = e.target.value;
              setPageSize(Number(selectedValue));
            }}
            className="mentors-row-custom-select-component pagination-select-container"
            style={{
              borderColor: COLORS.TEXT_PRIMARY,
              color: COLORS.TEXT_PRIMARY,
              backgroundColor: COLORS.SECTION_BACKGROUND,
            }}
            defaultValue={10}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={mentors?.total}>All</option>
          </select>
        </div>

        <div style={{ color: COLORS.TEXT_SECONDARY }} className="mentors-table-pagination-label">
          {`${(currentPage - 1) * pageSize + 1}-${Math.min(
            currentPage * pageSize,
            mentors?.total || 0
          )} of ${mentors?.total || 0}`}
        </div>

        {/*PAGINATION NAVIGATOR*/}
        <div>
          <Pagination
            className="custom-pagination"
            showSizeChanger={false}
            current={currentPage}
            defaultCurrent={1}
            total={mentors?.total}
            pageSize={pageSize}
            onChange={(page) => setCurrentPage(page)}
            prevIcon={
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <LeftOutlined
                  style={{
                    color: COLORS.TEXT_SECONDARY,
                  }}
                />
              </div>
            }
            nextIcon={
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <RightOutlined
                  style={{
                    color: COLORS.TEXT_SECONDARY,
                  }}
                />
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MentorsV3;
